/**
 * Canonical URL helpers — single source of truth for the site's public domain.
 *
 * RULE: Every route's `head()` MUST build canonical and og:url through these
 * helpers. Never hardcode "https://simpletechhelper.com" (or any preview /
 * staging domain) in a route file. If the domain ever changes, change SITE_URL
 * here and the whole site updates.
 *
 * Usage in a route:
 *
 *   import { canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";
 *
 *   head: ({ params }) => ({
 *     meta: [
 *       { title: "..." },
 *       seoOgUrl(`/articles/${params.slug}`),
 *     ],
 *     links: [seoCanonicalLink(`/articles/${params.slug}`)],
 *   })
 */

export const SITE_URL = "https://simpletechhelper.com";

/** Build an absolute URL on the canonical domain. */
export function canonical(path: string): string {
  if (!path || path === "/") return SITE_URL;
  const withLeading = path.startsWith("/") ? path : `/${path}`;
  // Strip trailing slash (except for root) to keep canonicals stable.
  const normalized =
    withLeading.length > 1 && withLeading.endsWith("/")
      ? withLeading.slice(0, -1)
      : withLeading;
  return `${SITE_URL}${normalized}`;
}

/** `<link rel="canonical">` entry for a route's `links` array. */
export function seoCanonicalLink(path: string) {
  return { rel: "canonical", href: canonical(path) } as const;
}

/** `og:url` entry for a route's `meta` array. */
export function seoOgUrl(path: string) {
  return { property: "og:url", content: canonical(path) } as const;
}

export const SITE_NAME = "SimpleTech";
const MAX_TITLE_LENGTH = 60;

/**
 * Append the site suffix to a page title — but only when it isn't already
 * present and the result still fits inside the ~60 char SERP limit.
 * RULE: never write `${title} | SimpleTech` inline in a route again.
 */
export function withSiteSuffix(title: string, siteName: string = SITE_NAME): string {
  const base = title.trim();
  if (!base) return siteName;
  if (base.toLowerCase().includes(siteName.toLowerCase())) return base;
  const suffixed = `${base} | ${siteName}`;
  return suffixed.length <= MAX_TITLE_LENGTH ? suffixed : base;
}
